import ModelFactory from 'business/models/ModelFactory';
import { LicenseClient, LicenseRequestInfo } from 'business/models/Models';
import copy from 'copy-html-to-clipboard';
import { FmlxBanner, FmlxButton, FmlxModal, FmlxRadioButton } from 'fmlx-common-ui';
import React, { useEffect, useState } from 'react';
import { useLocalStorage } from 'CustomHooks/useLocalStorage';
import { LICENSE_EXPIRED_KEY } from 'components/main/constants';
import { showSnackBar } from 'utils/Global';
import LicenseHeader from '../LicenseHeader';

enum RequestLicenseMethod {
  Online = 0,
  Manual = 1,
}

const HintLabels = [
  <div id="license-request-hint-labels-online" key="online-hint">
    Your request will be sent automatically to <b>support@formulatrix.com</b>
  </div>,
  <div id="license-request-hint-labels-manual" key="manual-hint">
    This action will copy the file and send it to email at <b>support@formulatrix.com</b>{' '}
    for request the license
  </div>,
];

const licenseClient: LicenseClient = ModelFactory(LicenseClient, false, true);

function copyToClipboard(data: any) {
  copy(data, {
    asHtml: false,
  });
}
function openEmailClient(licenseRequestInfo: LicenseRequestInfo) {
  const { licenseRequest, mailTo, subject } = licenseRequestInfo;
  const body = licenseRequest.split('\r\n').join('%0D%0A');
  document.location.href = 'mailto:' + mailTo + '?subject=' + subject + '&body=' + body;
}

const LicenseRequest: React.FC<{ closeModal(): any }> = ({ closeModal }) => {
  const [licenseExpired] = useLocalStorage<boolean>(LICENSE_EXPIRED_KEY, false);
  const [option, setOption] = useState<RequestLicenseMethod>(RequestLicenseMethod.Online);
  const [licenseIsActive, setLicenseActive] = useState<boolean>(false);
  const [showActiveLicenseConfirmation, setShowActiveLicenseConfirmation] =
    useState<boolean>(false);

  const optionChange = ({ index }) => {
    index = parseInt(index, 10);
    setOption(index === 0 ? RequestLicenseMethod.Online : RequestLicenseMethod.Manual);
  };
  const requestManual = async () => {
    const result = await licenseClient.requestLicenseManaual();
    if (result) {
      copyToClipboard(result.licenseRequest);
      openEmailClient(result);
      showSnackBar(
        'License has been successfully requested and copied to your clipboard',
      );
      closeModal();
    } else {
      showSnackBar('License request attempt failed. Try again later.');
    }
  };
  const requestOnline = async () => {
    const result = await licenseClient.requestLicenseOnline();
    if (result) {
      showSnackBar('License Request sent to Formulatrix Successfully.');
      closeModal();
    } else {
      showSnackBar('License request attempt failed. Try again later.');
    }
    setShowActiveLicenseConfirmation(false);
  };
  const requestButtonClicked = async () => {
    if (option === RequestLicenseMethod.Online) {
      if (licenseIsActive && !licenseExpired) {
        setShowActiveLicenseConfirmation(true);
      } else {
        await requestOnline();
      }
    } else if (option === RequestLicenseMethod.Manual) {
      await requestManual();
    }
  };
  const getLicenseIsActive = async () => {
    const licDetails = await licenseClient.getLicenseDetail(false);
    if (licDetails) {
      setLicenseActive(licDetails.isActive);
    }
  };
  useEffect(() => {
    getLicenseIsActive();
  }, []);
  const footer = showActiveLicenseConfirmation ? (
    <div
      id="license-request-footer-wrapper-confirm"
      className="mr-5 pr-1 mb-5 content-right-align"
    >
      <FmlxButton
        id="license-request-cancel-button"
        type={FmlxButton.Type.PRIMARY}
        variant={FmlxButton.Variant.OUTLINE}
        label="CANCEL"
        onClick={() => setShowActiveLicenseConfirmation(false)}
      />
      <FmlxButton
        id="license-request-continue-button"
        type={FmlxButton.Type.PRIMARY}
        label="CONTINUE"
        onClick={requestOnline}
      />
    </div>
  ) : (
    <div
      id="license-request-footer-wrapper-request"
      className="mr-5 pr-1 mb-5 content-right-align"
    >
      <FmlxButton
        id="license-request-back-button"
        type={FmlxButton.Type.PRIMARY}
        variant={FmlxButton.Variant.OUTLINE}
        label="BACK"
        onClick={closeModal}
      />
      <FmlxButton
        id="license-request-request-button"
        type={FmlxButton.Type.PRIMARY}
        label={
          option === RequestLicenseMethod.Online ? 'SEND REQUEST' : 'COPY ACTIVATION FILE'
        }
        onClick={requestButtonClicked}
      />
    </div>
  );
  return (
    <FmlxModal
      id="license-request-modal"
      open
      customHeader={<LicenseHeader />}
      customFooter={footer}
      primaryButton={{ show: false } as any}
      tertiaryButton={{ show: false } as any}
      secondaryButton={{ show: false } as any}
    >
      <h5 id="license-request-label" className="label-primary">
        Request License
      </h5>
      <hr />
      {showActiveLicenseConfirmation && (
        <FmlxBanner
          id="license-request-warning-banner"
          variant={FmlxBanner.Variant.WARNING}
          title="You have already an active license. Requesting it again will renew your license"
          subtitle=""
          hideCloseButton
          show
        />
      )}
      <div id="license-request-select-wrapper" className="px-5 mt-2">
        <div id="license-request-select-label" className="license-select-label">
          Please select the request license method:
        </div>
        <div id="license-request-radio-button">
          <FmlxRadioButton
            id="license-request-method-selection"
            size={FmlxRadioButton.Size.LARGE}
            selectedIndex={option}
            items={[
              {
                type: 'information',
                content: 'Online Activation',
                hint:
                  option === RequestLicenseMethod.Online ? (HintLabels[0] as any) : '',
                disabled: showActiveLicenseConfirmation,
              },
              {
                type: 'information',
                content: 'Manual Activation',
                hint:
                  option === RequestLicenseMethod.Manual ? (HintLabels[1] as any) : '',
                disabled: showActiveLicenseConfirmation,
              },
            ]}
            onChange={optionChange}
          />
        </div>
      </div>
      <hr />
    </FmlxModal>
  );
};

export default LicenseRequest;
